export const typesModule = `# 通用模組：CMS 資料型別定義

> 版本：v1.0
> 適用場景：任何需要內容管理系統的專案
> 輸入：業務需求描述
> 輸出：前後台共用的 TypeScript interface 集合

---

## 1. 設計原則

1. **單一真實來源**：型別定義只存在一個檔案，前後台透過 copy-paste 同步
2. **選擇性欄位標記 \`?\`**：所有可能為空的欄位必須標記為 optional
3. **前後台一致**：\`apps/web/src/types/cms.ts\` 與 \`apps/api/src/types.ts\` 必須逐字一致
4. **預設值**：\`default.ts\` 中的預設資料必須滿足型別的必填欄位

---

## 2. 通用 CMS 型別模板

\`\`\`ts
// === 列舉型別 ===
export type ListingType = 'sale' | 'rent' | 'sale_or_rent';
export type PropertyCategory = 'office' | 'industrial' | 'shop' | 'car_park';
export type District = 'tsuen_wan' | 'kwai_chung' | 'lai_chi_kok' | 'kowloon' | 'all';
export type InquiryType = 'owner_listing' | 'contact' | 'property_inquiry';
export type InquiryStatus = 'new' | 'contacted' | 'closed' | 'approved' | 'rejected';
export type AnalyticsEventType = 'pageview' | 'conversion';

// === 核心實體 ===
export interface Property {
  id: string;
  code: string;                    // 內部編號
  title: string;                   // 標題（預設語言）
  titleTraditional?: string;       // 繁體（可選）
  titleEnglish?: string;           // 英文（可選）
  listingType: ListingType;
  category: PropertyCategory;
  district: District;
  location: string;
  address: string;
  addressTraditional?: string;
  addressEnglish?: string;
  mapLink?: string;                // Google Maps 連結
  price: number;
  priceUnit: string;
  rentPrice?: number;
  rentPriceUnit?: string;
  areaGross: number;               // 建築面積
  areaSaleable?: number;           // 實用面積
  description: string;
  descriptionTraditional?: string;
  descriptionEnglish?: string;
  images: string[];                // 圖片 URL 陣列
  floorPlan?: string;              // 平面圖 URL
  tags: string[];
  features: string[];
  featured: boolean;               // 是否精選
  agentId: string;                 // 負責人 ID
  publishDate?: string;            // ISO 8601
}

export interface Agent {
  id: string;
  name: string;
  title: string;                   // 職稱
  whatsapp: string;                // 保留 852 前綴
  email?: string;
  photo?: string;                  // 照片 URL
}

export interface NewsItem {
  id: string;
  title: string;
  date: string;                    // ISO 8601 日期
  summary: string;
  content?: string;                // 完整內容（HTML/Markdown）
  image: string;                   // 封面圖 URL
  category: string;
}

export interface Inquiry {
  id: string;
  type: InquiryType;
  name: string;
  phone: string;
  whatsapp?: string;
  email?: string;
  address?: string;
  propertyType?: string;
  listingType?: string;
  area?: string;
  expectedPrice?: string;
  message?: string;
  propertyId?: string;             // 關聯物業 ID
  createdAt: string;               // ISO 8601
  status: InquiryStatus;
  rejectReason?: string;
}

export interface MediaFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;                    // bytes
  data?: string;                   // base64（KV 儲存時）
  url?: string;                    // R2 公開 URL
  createdAt: string;
}

export interface AnalyticsEvent {
  id: string;
  type: AnalyticsEventType;
  path: string;                    // 頁面路徑
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  sessionId: string;
  country?: string;
  city?: string;
  timestamp: string;               // ISO 8601
  meta?: Record<string, any>;
}

// === 網站設定 ===
export interface SiteConfig {
  name: string;
  nameEnglish: string;
  tagline: string;
  description: string;
  logo: string;
  favicon?: string;
  social: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    wechat?: string;
  };
}

export interface NavItem {
  id: string;
  label: string;
  href: string;
  children?: NavItem[];
}

export interface HeroSection {
  backgroundImage: string;
  tagline: string;
  titleLine1: string;
  titleLine2: string;
  description: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
}

export interface AboutSection {
  enabled: boolean;
  sectionTagline: string;
  sectionTitle: string;
  companyName: string;
  companyNameEnglish: string;
  establishedYear: string;
  description: string;
  businessScope: string[];
  image: string;
}

export interface ServiceItem {
  id: string;
  icon: string;                    // Lucide icon name
  title: string;
  description: string;
}

export interface ServicesSection {
  enabled: boolean;
  sectionTagline: string;
  sectionTitle: string;
  sectionDescription: string;
  items: ServiceItem[];
}

export interface ContactInfo {
  phone: string;
  phoneDisplay: string;            // 格式化顯示用
  whatsapp: string[];
  email: string;
  address: string;
  addressDisplay: string;          // 格式化顯示用
  businessHours: string;
  mapLink?: string;
}

export interface ContactSection {
  enabled: boolean;
  sectionTagline: string;
  sectionTitle: string;
  sectionDescription: string;
  info: ContactInfo;
}

export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'tel' | 'email' | 'select' | 'textarea' | 'number';
  placeholder?: string;
  required: boolean;
  options?: { label: string; value: string }[];
}

export interface OwnerFormSection {
  enabled: boolean;
  title: string;
  description: string;
  fields: FormField[];
  whatsappNotificationNumber?: string;
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterSection {
  companyName: string;
  companyNameEnglish: string;
  description: string;
  copyright: string;
  licenseNumber: string;
  links: FooterLink[];
}

// === 頂層 CMS 資料 ===
export interface CMSData {
  siteConfig: SiteConfig;
  nav: NavItem[];
  hero: HeroSection;
  about: AboutSection;
  services: ServicesSection;
  properties: Property[];
  agents: Agent[];
  news: NewsItem[];
  contact: ContactSection;
  ownerForm: OwnerFormSection;
  footer: FooterSection;
  lastUpdated?: string;
}
\`\`\`

---

## 3. 型別同步檢查清單

當修改任何型別時，必須檢查以下檔案：

| 檔案 | 用途 | 檢查項目 |
|---|---|---|
| \`apps/web/src/types/cms.ts\` | 前台型別 | 與 api 版本逐字一致 |
| \`apps/api/src/types.ts\` | 後台型別 | 與 web 版本逐字一致 |
| \`apps/api/src/data/default.ts\` | 預設資料 | 所有必填欄位都有值 |
| \`apps/web/src/data/cms.json\` | 前台備份 | 符合 CMSData 結構 |

---

## 4. 變更影響矩陣

\`\`\`
Property 欄位變更
  ├── 影響：前台 PropertyCard.tsx
  ├── 影響：前台 PropertyDetail.tsx
  ├── 影響：前台 PropertyGrid.tsx（篩選邏輯）
  ├── 影響：後台 PropertyEditor.tsx
  ├── 影響：後台 PropertyManager.tsx
  └── 影響：default.ts 中的示例物業

Agent 欄位變更
  ├── 影響：前台 WhatsApp 按鈕連結
  ├── 影響：後台 銷售團隊管理
  └── 影響：default.ts 中的初始團隊
\`\`\`
`;
